import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  ComponentType,
  ReactNode,
  forwardRef,
} from 'react'
import styled from 'styled-components'
import { useTrackVisibility } from 'react-intersection-observer-hook'
import has from 'lodash/has'
import debounce from 'lodash/debounce'

import useDispatch from 'hooks/useDispatch'

import { resetSecondLevelFilters } from '../../actions/searchActions'
import {
  SearchQueryHookResult,
  useGetSelectEscapeIndexesQuery,
} from '../../hooks/useQueries'
import { media, mediaMax } from '../../styles/media'
import typeStyle from '../../styles/typeStyle'

import Button from '../../components/display/Button'
import SimpleA from '../../components/display/SimpleAnchor'
import PlaceholderCard from '../../components/cards/PlaceholderCard'
import DatePrice from '../../components/icons/brand/DatePrice'
import DatePopular from '../../components/icons/brand/DatePopular'
import InlineAlert from '../../components/display/InlineAlert'

/**
 * We need to disable scroll anchoring so when the user is scrolled
 * all the way down on chrome adding more elements to the page
 * scroll the user up so we can keep the load more loop going
 */
import DisableScrollAnchoring from '../../components/utils/DisableScrollAnchoring'
import {
  Grid,
  GridItem,
  GridSkeletonRow,
} from '../../components/layout/ItemGrid'

import useDriveTimeFilterAvailable from '../useDriveTimeFilterAvailable'

import Empty from './Empty'
import Result from './Result'
import buildApologiesMessage from './buildApologiesMessage'
import { SEARCH_PAGE_SIZE } from '../constants'

const CustomActionWell = styled(InlineAlert)`
  ${typeStyle('-ds-font-lead-xs')}
  margin: 0 var(--ds-space-05);
  align-items: center;

  ${media.md`
    margin: 0;
  `}
`

const RemoveFiltersButton = styled(Button).attrs({
  rounded: true,
  ghost: true,
})`
  margin: 0 auto;
`

const LinkWrapper = styled.p`
  ${typeStyle('-ds-font-lead')}
  margin-top: 24px;
`

const Outer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-06);
`

const ResultSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-04);
`

const ResultTypeHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-02);

  ${mediaMax.md`
    padding: 0 var(--ds-space-05);
  `}

  h3 {
    ${typeStyle('-ds-font-title-3-regular')}
    margin: 0;
  }

  p {
    margin: 0;
  }
`

const SECTION_HEADINGS: Record<
  string,
  { title: string; blurb: string } | undefined
> = {
  stories: {
    title: 'Curated escapes',
    blurb: 'Be inspired by real trips taken by Riparide Storytellers.',
  },
  listings: {
    title: 'Explore listings',
    blurb: 'Discover all the unique listings you can stay at in this region.',
  },
}

const sectionKeyFor = (typename: string): string => {
  if (typename === 'Story') return 'stories'
  if (typename === 'Listing') return 'listings'
  return 'other'
}

const noResultsMessageForSearchType = (
  searchType: GQL_SearchFilters['type'],
): ReactNode => {
  switch (searchType) {
    case 'accommodation':
      return "Uh-oh! We can't find any stays that match your search."
    case 'adventures':
      return "Uh-oh! We can't find any adventures that match your search."
    default:
      return undefined
  }
}

export const EmptySearchActions = ({
  onResetFilters,
}: {
  onResetFilters: () => any
}) => (
  <>
    <RemoveFiltersButton onClick={onResetFilters}>
      Remove Filters
    </RemoveFiltersButton>
    <LinkWrapper>
      Take me <SimpleA href="/">back home.</SimpleA>
    </LinkWrapper>
  </>
)

type LoaderProps = {
  variant?: 'Listing' | 'Story' | 'StoryAdventure' | 'ListingAdventure'
}

// Overshoot the widest possible row; GridSkeletonRow clips to one row.
const SKELETON_CARD_COUNT = 8

const Loader = forwardRef<HTMLDivElement, LoaderProps>(
  ({ variant = 'Story' }, ref) => (
    <GridSkeletonRow>
      {[...Array(SKELETON_CARD_COUNT)].map((e, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <GridItem key={i} ref={i === 0 ? ref : undefined}>
          <PlaceholderCard portrait={variant === 'Story'} />
        </GridItem>
      ))}
    </GridSkeletonRow>
  ),
)

type Data = SearchQueryHookResult['data']
type Defaults = Data['search']['defaults']
type ResultsType = Data['search']['results']

interface ResultsProps {
  defaults: Defaults
  fetchMore: (...args: any) => any
  filters: GQL_SearchFilters
  results: ResultsType
  loading?: boolean
  isDesktop?: boolean
}

const Results = ({
  defaults,
  fetchMore,
  filters,
  loading,
  isDesktop,
  results,
}: ResultsProps) => {
  const dispatch = useDispatch()
  const { available: showDriveTime } = useDriveTimeFilterAvailable(filters)

  const isNZ = filters?.country === 'NZ'

  // @ts-ignore -- resetSecondLevelFilters not typed yet
  const onResetFilters = () =>
    dispatch(resetSecondLevelFilters({ preserveStateParam: !isNZ }))

  const { data: selectEscapeIndexesData } = useGetSelectEscapeIndexesQuery()

  const bucketListItemsIndex = selectEscapeIndexesData?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex

  const bookingAdventuresIndex = selectEscapeIndexesData?.currentUser
    ?.bookingAdventuresIndex as GQL_BookingAdventuresIndex

  const hasSpecificDates = has(filters, 'startDate') && has(filters, 'endDate')
  const hasWeekendMonths = has(filters, 'weekendMonths')

  const hasDatesFilters = hasSpecificDates || hasWeekendMonths

  const hideTotalPricePrompt =
    (filters && ['stories', 'adventures'].indexOf(filters.type) > -1) ||
    hasDatesFilters

  const showPercentageAvailable =
    hideTotalPricePrompt &&
    defaults &&
    defaults.percentageAvailable <= 50 &&
    defaults.percentageAvailable > 0 &&
    results &&
    results.edges.length > 0

  const noResultsMessage = noResultsMessageForSearchType(filters.type)

  const apologiesMessage = buildApologiesMessage(filters)

  const lastResult =
    results?.edges?.[(results?.edges?.length ?? 0) - 1]?.node?.result
  const loaderVariant = lastResult?.__typename

  const outerRef = useRef<HTMLDivElement>()

  const [ref, { isVisible }] = useTrackVisibility({
    rootMargin: isDesktop ? '10%' : '75% 0px',
  })

  const hasNextPage = results?.pageInfo?.hasNextPage

  useLayoutEffect(() => {
    if (isVisible && hasNextPage) {
      fetchMore()
    }
  }, [isVisible, hasNextPage])

  const sections: Array<{
    key: string
    items: Array<{ node: ResultsType['edges'][0]['node']; index: number }>
  }> = []

  results?.edges.forEach(({ node }, index) => {
    const key = sectionKeyFor(node.result.__typename)
    let section = sections.find((s) => s.key === key)

    if (!section) {
      section = { key, items: [] }
      sections.push(section)
    }

    section.items.push({ node, index })
  })

  return (
    <Outer ref={outerRef} data-testid="results-grid">
      <DisableScrollAnchoring />
      {!hideTotalPricePrompt &&
        !hasDatesFilters &&
        (loading || results?.edges?.length > 0) && (
          <CustomActionWell variant="information" iconWidth={32}>
            <DatePrice />
            <div>
              Select your preferred dates to see the total price inclusive of
              any additional charges and fees.
            </div>
          </CustomActionWell>
        )}
      {showPercentageAvailable && (
        <CustomActionWell variant="warning" iconWidth={32}>
          <DatePopular />
          <div>
            <strong>These dates are popular!</strong> Only{' '}
            {defaults.percentageAvailable}% of escapes are still available.
          </div>
        </CustomActionWell>
      )}

      {loading && !results && (
        <Grid>
          <Loader variant={loaderVariant} />
        </Grid>
      )}

      {!loading && results?.edges.length === 0 && (
        <Empty
          message={noResultsMessage}
          apologiesMessage={apologiesMessage}
          actions={<EmptySearchActions onResetFilters={onResetFilters} />}
        />
      )}

      {sections.map((section, sectionIndex) => {
        const heading = SECTION_HEADINGS[section.key]

        return (
          <ResultSection key={section.key}>
            {heading && (
              <ResultTypeHeading>
                <h3>{heading.title}</h3>
                <p>{heading.blurb}</p>
              </ResultTypeHeading>
            )}
            <Grid>
              {section.items.map(({ node, index }) => (
                <Result
                  key={`${node.result.__typename}:${node.result.id}`}
                  bookingAdventuresIndex={bookingAdventuresIndex}
                  bucketListItemsIndex={bucketListItemsIndex}
                  result={node.result}
                  pricing={node.pricing}
                  discount={node.discount}
                  availableDates={hasWeekendMonths && node?.availableDates}
                  filters={filters}
                  isMobile={!isDesktop}
                  eager={index < SEARCH_PAGE_SIZE}
                  showDriveTime={showDriveTime}
                />
              ))}
              {hasNextPage && sectionIndex === sections.length - 1 && (
                <Loader ref={ref} variant={loaderVariant} />
              )}
            </Grid>
          </ResultSection>
        )
      })}
    </Outer>
  )
}

const withDebouncedResults =
  (BaseComponent: ComponentType<ResultsProps>) =>
  ({ results, ...props }: ResultsProps) => {
    const [debouncedResults, setDebouncedResults] = useState(null)

    const handleChange = useCallback(
      debounce(() => {
        setDebouncedResults(results)
      }, 100),
      [results],
    )

    useEffect(() => {
      handleChange()

      return () => {
        handleChange.cancel()
      }
    }, [results])

    return <BaseComponent results={debouncedResults || results} {...props} />
  }

export default withDebouncedResults(Results)
